// Shared email/password/Google sign-in form. Used by both the onboarding
// sign-in step (`layoutSize="card"`) and the standalone sign-in screen
// (`layoutSize="fullscreen"`). Three modes — `signin`, `signup`, and
// `forgot` — toggle inside the same component so the user can switch between
// "I'm new" and "I forgot my password" without leaving the form.
//
// Password is the primary path; Google sign-in lives below a divider as an
// alternative and only shows in `signin` mode (it makes no sense for the
// other two flows).

import { useState } from 'react';
import { Eye, EyeOff, Sparkles } from 'lucide-react';
import { getAuth } from '@/lib/auth/auth';
import { C } from '@/styles/tokens';

interface EmailAuthFormProps {
  /** Adjusts spacing/typography for the fullscreen vs onboarding-card hosts. */
  layoutSize?: 'card' | 'fullscreen';
}

type Mode = 'signin' | 'signup' | 'forgot';

type Status =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'confirmEmailSent'; email: string }
  | { kind: 'resetEmailSent'; email: string }
  | { kind: 'error'; message: string };

const MIN_PASSWORD = 8;

// Map Supabase's raw error strings to friendlier copy. Anything we don't
// recognise falls through to the original message — better to show something
// than swallow the diagnostic.
function friendlyError(raw: string): string {
  const m = raw.toLowerCase();
  if (m.includes('invalid login credentials')) return "That email and password don't match.";
  if (m.includes('email not confirmed')) return 'Please confirm your email first — check your inbox for the link.';
  if (m.includes('user already registered')) return 'An account with that email already exists. Try signing in instead.';
  if (m.includes('password should be at least')) return `Password must be at least ${MIN_PASSWORD} characters.`;
  if (m.includes('rate limit')) return 'Too many attempts. Please wait a minute and try again.';
  if (m.includes('unable to validate email')) return 'That email address looks invalid.';
  return raw;
}

export function EmailAuthForm({ layoutSize = 'fullscreen' }: EmailAuthFormProps) {
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  const isSubmitting = status.kind === 'submitting';
  const isFullscreen = layoutSize === 'fullscreen';

  function resetStatus() {
    setStatus({ kind: 'idle' });
  }

  function switchMode(next: Mode) {
    setMode(next);
    setStatus({ kind: 'idle' });
    setPassword('');
    setShowPassword(false);
  }

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) return;
    setStatus({ kind: 'submitting' });
    try {
      await getAuth().signInWithPassword(trimmedEmail, password);
      // Successful sign-in flips the auth subscriber; this component will
      // unmount via App.tsx routing. No success state needed here.
    } catch (err) {
      setStatus({ kind: 'error', message: friendlyError((err as Error).message) });
    }
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) return;
    if (password.length < MIN_PASSWORD) {
      setStatus({ kind: 'error', message: `Password must be at least ${MIN_PASSWORD} characters.` });
      return;
    }
    setStatus({ kind: 'submitting' });
    try {
      const result = await getAuth().signUpWithPassword(trimmedEmail, password);
      if (result.kind === 'confirmation_sent') {
        setStatus({ kind: 'confirmEmailSent', email: trimmedEmail });
      }
      // If signed in immediately, App.tsx routing takes over.
    } catch (err) {
      setStatus({ kind: 'error', message: friendlyError((err as Error).message) });
    }
  }

  async function handleForgot(e: React.FormEvent) {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;
    setStatus({ kind: 'submitting' });
    try {
      await getAuth().requestPasswordReset(trimmedEmail);
      setStatus({ kind: 'resetEmailSent', email: trimmedEmail });
    } catch (err) {
      setStatus({ kind: 'error', message: friendlyError((err as Error).message) });
    }
  }

  async function handleGoogle() {
    setStatus({ kind: 'submitting' });
    try {
      await getAuth().signInWithGoogle();
      // OAuth navigates away — if we're still here, treat as error.
    } catch (err) {
      setStatus({ kind: 'error', message: friendlyError((err as Error).message) });
    }
  }

  // "Check your inbox" cards share a layout — two variants, one renderer.
  if (status.kind === 'confirmEmailSent' || status.kind === 'resetEmailSent') {
    const copy =
      status.kind === 'confirmEmailSent'
        ? { tag: 'Confirm your email', body: <>We sent a confirmation link to <strong>{status.email}</strong>. Click it to finish creating your account.</> }
        : { tag: 'Check your inbox', body: <>We sent a password-reset link to <strong>{status.email}</strong>. Click it to set a new password.</> };
    return (
      <div className={isFullscreen ? 'w-full' : ''}>
        <div
          className="p-4 rounded-2xl anim-scale-in"
          style={{
            background: 'white',
            border: `1px solid ${C.mist}`,
            boxShadow: '0 2px 10px rgba(26,22,20,0.06)',
            textAlign: isFullscreen ? 'left' : 'left',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} aria-hidden style={{ color: C.vermillion }} />
            <span
              className="ff-body text-[10px] uppercase tracking-[0.2em] font-bold"
              style={{ color: C.vermillion }}
            >
              {copy.tag}
            </span>
          </div>
          <p className="ff-body text-[13px] leading-relaxed" style={{ color: C.ink }}>
            {copy.body}
          </p>
          <button
            type="button"
            onClick={resetStatus}
            className="ff-body text-[11px] mt-3 underline"
            style={{ color: C.inkLight }}
          >
            Use a different email
          </button>
        </div>
      </div>
    );
  }

  const submitHandler =
    mode === 'signin' ? handleSignIn : mode === 'signup' ? handleSignUp : handleForgot;
  const submitLabel =
    mode === 'signin' ? 'Sign in' : mode === 'signup' ? 'Create account' : 'Send reset link';
  const headingForCard =
    mode === 'signup' ? 'Create your account' : mode === 'forgot' ? 'Reset your password' : null;

  return (
    <div className={isFullscreen ? 'w-full' : 'flex flex-col gap-3'}>
      {/* Card host shows a small heading when not in default signin mode so
          the mode switch is obvious. Fullscreen relies on the parent heading. */}
      {!isFullscreen && headingForCard && (
        <div className="ff-body text-[11px] uppercase tracking-[0.25em]" style={{ color: C.inkLight }}>
          {headingForCard}
        </div>
      )}

      <form onSubmit={submitHandler} className={isFullscreen ? 'w-full' : ''}>
        <label
          htmlFor="auth-email"
          className="block ff-body text-[10px] uppercase tracking-[0.25em] mb-2"
          style={{ color: C.inkLight }}
        >
          Email
        </label>
        <input
          id="auth-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          required
          className="w-full px-3.5 py-3 rounded-2xl border ff-body text-[14px] outline-none mb-3"
          style={{
            background: 'white',
            borderColor: email ? C.vermillion : 'rgba(0,0,0,0.08)',
            color: C.ink,
          }}
        />

        {mode !== 'forgot' && (
          <>
            <label
              htmlFor="auth-password"
              className="block ff-body text-[10px] uppercase tracking-[0.25em] mb-2"
              style={{ color: C.inkLight }}
            >
              Password
            </label>
            <div className="relative mb-3">
              <input
                id="auth-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'signup' ? `At least ${MIN_PASSWORD} characters` : '••••••••'}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                required
                minLength={mode === 'signup' ? MIN_PASSWORD : undefined}
                className="w-full px-3.5 py-3 pr-11 rounded-2xl border ff-body text-[14px] outline-none"
                style={{
                  background: 'white',
                  borderColor: password ? C.vermillion : 'rgba(0,0,0,0.08)',
                  color: C.ink,
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full"
                style={{ color: C.inkLight }}
              >
                {showPassword ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
              </button>
            </div>
          </>
        )}

        <button
          type="submit"
          disabled={
            isSubmitting ||
            !email.trim() ||
            (mode !== 'forgot' && !password)
          }
          className="w-full py-3 rounded-2xl ff-body text-[13px] font-bold uppercase tracking-[0.2em] text-white inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
          style={{ background: C.vermillion }}
        >
          {isSubmitting ? '…' : submitLabel}
        </button>
      </form>

      {/* Sub-links under the form: mode-flips and forgot-password trigger. */}
      <div className="flex items-center justify-between gap-2 mt-2 px-1">
        {mode === 'signin' && (
          <>
            <button
              type="button"
              onClick={() => switchMode('forgot')}
              className="ff-body text-[11px] underline underline-offset-2"
              style={{ color: C.inkLight }}
            >
              Forgot password?
            </button>
            <button
              type="button"
              onClick={() => switchMode('signup')}
              className="ff-body text-[11px] underline underline-offset-2"
              style={{ color: C.inkLight }}
            >
              Create account
            </button>
          </>
        )}
        {mode === 'signup' && (
          <button
            type="button"
            onClick={() => switchMode('signin')}
            className="ff-body text-[11px] underline underline-offset-2 ml-auto"
            style={{ color: C.inkLight }}
          >
            Already have an account? Sign in
          </button>
        )}
        {mode === 'forgot' && (
          <button
            type="button"
            onClick={() => switchMode('signin')}
            className="ff-body text-[11px] underline underline-offset-2 ml-auto"
            style={{ color: C.inkLight }}
          >
            Back to sign in
          </button>
        )}
      </div>

      {/* Alternative sign-in methods live below a divider, in signin mode only. */}
      {mode === 'signin' && (
        <>
          <div className="flex items-center gap-2 my-3" aria-hidden>
            <span className="flex-1 h-px" style={{ background: 'rgba(26,22,20,0.1)' }} />
            <span className="ff-body text-[10px] uppercase tracking-[0.25em]" style={{ color: C.inkSofter }}>
              or
            </span>
            <span className="flex-1 h-px" style={{ background: 'rgba(26,22,20,0.1)' }} />
          </div>

          <button
            type="button"
            onClick={handleGoogle}
            disabled={isSubmitting}
            className="w-full py-3 rounded-2xl ff-body text-[13px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: 'white', border: '1px solid rgba(0,0,0,0.08)', color: C.ink }}
          >
            <GoogleIcon />
            Continue with Google
          </button>
        </>
      )}

      {status.kind === 'error' && (
        <p
          role="alert"
          className="ff-body text-[11px] mt-3 px-1"
          style={{ color: C.vermillionDeep }}
        >
          {status.message}
        </p>
      )}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden>
      <path
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  );
}
